/** Know your data — what Signals has worked out about this company's data.
 *
 * Every word and number comes from the server (backend/chainlit/
 * know_your_data.py), counted from the tenant's own tables with fixed SQL; this
 * page only lays the blocks out. English by default; the Arabic switch shows
 * only when the server offers Arabic (never to an english_only user).
 *
 * Socketless (App.tsx SOCKETLESS_ROUTES). "Ask about this" opens a new chat
 * with the question typed in; a ready-made analysis opens its form.
 * Open to every company user; the server refuses admins.
 */
import { Check, FileDown, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Header } from '@/components/header';
import {
  KnowYourDataPage,
  Lang,
  fetchKnowYourData,
  pickLang,
  saveBusinessProfile,
  storeLang,
  storedLang
} from '@/components/know-your-data/api';
import {
  Building,
  LangContext,
  LanguageSwitch,
  SectionView,
  surface
} from '@/components/know-your-data/blocks';
import { pdfTitle, savePageAsPdf } from '@/components/know-your-data/print';
import { SidebarProvider } from '@/components/ui/sidebar';

const CARD_DISMISSED = 'signals.kyd.profileCardDismissed';
const POLL_MS = 4000;
const MAX_POLLS = 30;

export default function KnowYourData() {
  const [lang, setLang] = useState<Lang>(storedLang);
  const [page, setPage] = useState<KnowYourDataPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const polls = useRef(0);

  const load = useCallback((wanted: Lang) => {
    setError(null);
    fetchKnowYourData(wanted)
      .then((p) => {
        setPage(p);
        if (p.building && polls.current < MAX_POLLS) {
          polls.current += 1;
          window.setTimeout(() => load(wanted), POLL_MS);
        }
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Could not load this page.')
      );
  }, []);

  useEffect(() => {
    load(lang);
  }, [lang, load]);

  const languages = page?.languages ?? ['en'];
  const choose = (next: Lang) => {
    const allowed = pickLang(next, languages);
    storeLang(allowed);
    polls.current = 0;
    setLang(allowed);
  };
  // What the server answered in: an english_only user asked for Arabic still gets English.
  const shown: Lang = page?.lang ?? lang;
  const dir = page?.dir ?? (shown === 'ar' ? 'rtl' : 'ltr');

  return (
    <SidebarProvider>
      {/* Printing (Save as PDF) lays the page out as one long document: no
          header, no fixed height, nothing that only works on screen. */}
      <div className="flex h-screen w-full flex-col [-webkit-print-color-adjust:exact] [print-color-adjust:exact] print:block print:h-auto">
        <div className="print:hidden">
          <Header />
        </div>
        <div
          className="flex-1 overflow-auto print:overflow-visible"
          id="kyd-scroll"
        >
          <div
            className="mx-auto max-w-3xl px-4 py-4 sm:px-6 sm:py-8"
            dir={dir}
            lang={shown}
          >
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <p
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--sig-blue)' }}
                >
                  {shown === 'ar' ? 'اعرف بياناتك' : 'Know your data'}
                </p>
                <h1 className="text-2xl font-semibold leading-snug text-[var(--sig-ink)]">
                  {page?.title ??
                    (shown === 'ar'
                      ? 'اللي Signals فهمه عن شغلك'
                      : 'What Signals understands about your business')}
                </h1>
                {page?.subtitle ? (
                  <p className="text-sm text-muted-foreground">
                    {page.subtitle}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2 print:hidden">
                {page?.sections?.length && !page.building ? (
                  <SavePdf lang={shown} />
                ) : null}
                {languages.includes('ar') ? (
                  <LanguageSwitch lang={lang} onChange={choose} />
                ) : null}
              </div>
            </div>

            {error ? (
              <div className="rounded-xl border p-4 text-sm" style={surface}>
                <p className="text-[var(--sig-ink)]">{error}</p>
                <button
                  type="button"
                  className="mt-2 text-sm font-medium text-primary hover:underline"
                  onClick={() => load(lang)}
                >
                  Try again
                </button>
              </div>
            ) : !page || page.building ? (
              <Building lang={shown} building={!!page?.building} />
            ) : (
              <LangContext.Provider value={page.lang}>
                <Body page={page} lang={page.lang} />
                <p className="hidden pt-6 text-xs text-muted-foreground print:block">
                  {page.lang === 'ar'
                    ? `اتعملت من Signals يوم ${new Date().toLocaleDateString('ar-EG')}.`
                    : `Made by Signals on ${new Date().toLocaleDateString('en-GB')}.`}
                </p>
              </LangContext.Provider>
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

function SavePdf({ lang }: { lang: Lang }) {
  return (
    <button
      type="button"
      onClick={() => savePageAsPdf(pdfTitle())}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      style={surface}
    >
      <FileDown className="size-3.5" />
      {lang === 'ar' ? 'حفظ PDF' : 'Save as PDF'}
    </button>
  );
}

function Body({ page, lang }: { page: KnowYourDataPage; lang: Lang }) {
  const sections = page.sections ?? [];
  const cardAt = sections.some((s) => s.id === 'market') ? 'market' : 'blind';
  const navRef = useRef<HTMLDivElement>(null);
  const jump = (id: string) => {
    const target = document.getElementById(`kyd-${id}`);
    if (!target) return;
    // The chips wrap onto two or three rows on wider screens, so the sticky
    // bar's height varies; a fixed scroll margin would hide the heading.
    const bar = navRef.current?.offsetHeight ?? 48;
    target.style.scrollMarginTop = `${bar + 8}px`;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!sections.length) {
    return (
      <div
        className="rounded-xl border p-6 text-sm text-muted-foreground"
        style={surface}
      >
        {lang === 'ar'
          ? 'لسه مفيش بيانات كفاية نبني منها الصفحة دي.'
          : 'There is not enough data yet to build this page.'}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {/* All chips in view from `sm` up, wrapped. On a phone they swipe
          sideways with no scrollbar; the fade at the far edge says there is
          more. */}
      <div
        ref={navRef}
        className="sticky top-0 z-10 -mx-4 mb-2 bg-background sm:-mx-6 print:hidden"
      >
        <nav className="flex gap-2 overflow-x-auto px-4 py-2 [scrollbar-width:none] sm:flex-wrap sm:overflow-visible sm:px-6 [&::-webkit-scrollbar]:hidden">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => jump(s.id)}
              className="shrink-0 rounded-full border px-3 py-1 text-xs font-medium text-[var(--sig-body)] hover:bg-[var(--sig-tint)]"
              style={{
                background: 'var(--sig-chip)',
                borderColor: 'var(--sig-border-blue)'
              }}
            >
              {s.title}
            </button>
          ))}
        </nav>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 end-0 w-10 bg-gradient-to-r from-transparent to-background rtl:bg-gradient-to-l sm:hidden"
        />
      </div>
      {sections.map((s) => (
        <SectionView key={s.id} section={s}>
          {s.id === cardAt && page.profile_card ? (
            <ProfileCard card={page.profile_card} lang={lang} />
          ) : null}
        </SectionView>
      ))}
    </div>
  );
}

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(CARD_DISMISSED) === '1';
  } catch {
    return false;
  }
}

/** "Is this right?" - Signals' guess at the company's priced commodities.
 *  Optional: the page works the same if it is never answered. */
function ProfileCard({
  card,
  lang
}: {
  card: { keys: string[]; names: string[] };
  lang: Lang;
}) {
  const navigate = useNavigate();
  const [state, setState] = useState<'ask' | 'saving' | 'done' | 'hidden'>(
    readDismissed() ? 'hidden' : 'ask'
  );
  if (state === 'hidden') return null;
  const ar = lang === 'ar';

  const confirm = () => {
    setState('saving');
    saveBusinessProfile({ input_commodities: card.keys }, lang)
      .then(() => setState('done'))
      .catch((e) => {
        setState('ask');
        toast.error(e instanceof Error ? e.message : 'Could not save.');
      });
  };
  const dismiss = () => {
    try {
      window.localStorage.setItem(CARD_DISMISSED, '1');
    } catch {
      /* shown again next time */
    }
    setState('hidden');
  };

  return (
    <div
      className="relative space-y-2.5 rounded-xl border p-4 print:hidden"
      style={{
        background: 'var(--sig-chip)',
        borderColor: 'var(--sig-border-blue)'
      }}
    >
      {state === 'done' ? (
        <p className="flex items-center gap-2 text-sm text-[var(--sig-ink)]">
          <Check className="size-4" style={{ color: 'var(--sig-positive)' }} />
          {ar ? 'اتحفظ. تقدر تغيره من ' : 'Saved. Change it any time in '}
          <button
            type="button"
            className="font-medium text-primary hover:underline"
            onClick={() => navigate('/business-profile')}
          >
            {ar ? 'نشاط شركتك' : 'Business profile'}
          </button>
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={dismiss}
            aria-label={ar ? 'اقفل' : 'Close'}
            className="absolute end-2 top-2 rounded-md p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="size-4" />
          </button>
          <p className="pe-7 text-sm font-semibold text-[var(--sig-ink)]">
            {ar
              ? 'عايز أختار مؤشرات السوق اللي تخصك بدقة أكتر'
              : 'Help Signals pick the market prices that matter to you'}
          </p>
          <p className="text-sm text-[var(--sig-body)]">
            {ar
              ? 'من مجموعات أصنافك، أهم خامات عندك ليها سعر عالمي هي:'
              : 'From your item groups, the materials you deal in that have a world price are:'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {card.names.map((n) => (
              <span
                key={n}
                className="rounded-full border px-2.5 py-0.5 text-xs text-[var(--sig-ink)]"
                style={{
                  background: 'var(--sig-surface)',
                  borderColor: 'var(--sig-border-blue)'
                }}
              >
                {n}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={confirm}
              disabled={state === 'saving'}
              className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-60"
            >
              {ar ? 'أيوه، صح' : 'Yes, that is right'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/business-profile')}
              className="rounded-full border px-4 py-1.5 text-xs font-semibold text-primary"
              style={{ borderColor: 'var(--sig-border-blue)' }}
            >
              {ar ? 'عدّل' : 'Change'}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {ar
              ? 'مش لازم ترد عشان الصفحة تشتغل. لما تأكد، الشات بيقارن أسعار الخامات دي بتكلفتك.'
              : 'You do not have to answer for this page to work. Once you confirm, the chat compares these prices with your costs.'}
          </p>
        </>
      )}
    </div>
  );
}
